import { useId, useEffect, useState } from "react";

/**
 * XBall - the Xelanti loading ball. Replaces the 🏀 emoji: an orange ball with
 * a pale lens panel, the Xelanti X pressed into it, and a pair of eyelids that
 * blink twice and then rest.
 *
 * Vector, so it stays sharp at any size and costs no image download. The bounce
 * is NOT here - callers pass the animation class (`nutri-ball` for the bouncing
 * loader, `animate-spin` for tiny in-button spinners), as they did with the
 * emoji.
 *
 * PAINT ORDER IS LOAD-BEARING. Bottom to top:
 *   1. the sphere
 *   2. the lens panel, sunk in with an inner shadow
 *   3. the X, clipped to the lens so it cannot run past the eyelid
 *   4. the lids - cut from the ball, carrying the seam as their own edge, and
 *      drawn LAST so they close OVER the X
 *   5. the gloss highlight, clipped to the sphere minus the lens
 *
 * HOW THE BLINK WORKS, and the two approaches that do not
 * -------------------------------------------------------
 * The eyeball must not change size - real lids come down over it. So:
 *
 * - CSS scaleY on the eye group squashes the eyeball itself. That reads as the
 *   eye being crushed, not closing. (It also needs transform-box:fill-box, or
 *   50% means the middle of the SVG rather than of the shape, and the eye
 *   slides off the ball.)
 * - Translating two rigid lid shapes toward the centre moves the LEFT AND
 *   RIGHT CORNERS too. An eyelid hinges at its corners. That reads as a
 *   shutter.
 *
 * What works is animating the path data. Each lid morphs between an OPEN path
 * and a SHUT path in which every control point is pulled onto the centre line,
 * while the two END points are byte-identical in both. Identical endpoints =
 * pinned corners = a hinge.
 *
 * The L92 -14 / L8 114 overshoot exists so the lid still covers when shut; it
 * is clipped to the ball, so the overshoot never shows.
 *
 * THE GRADIENT TRAP: the lids are filled with the ball's own gradient, and an
 * objectBoundingBox gradient (cx="35%") travels with its shape - whose bounding
 * box changes every frame - so a closing lid LIGHTS UP. gradientUnits=
 * "userSpaceOnUse" with absolute values fixes it. In a 0..100 viewBox the
 * numbers are identical to the percentages they replace.
 *
 * Ids are per-instance (useId). Two balls sharing an id means the second
 * silently borrows the first's animation and they blink in lockstep - and the
 * `begin="...;id.end+..."` chain would reference the wrong element entirely.
 *
 * SMIL ignores prefers-reduced-motion, so it is honoured by not rendering the
 * <animate> children at all rather than by CSS.
 */

const LENS = "M8 50 Q26 26 50 22 Q74 26 92 50 Q74 74 50 78 Q26 74 8 50 Z";

/* Lid bodies: the arc, then out past the edge of the ball. */
const UP_OPEN = "M8 50 Q26 26 50 22 Q74 26 92 50 L92 -14 L8 -14 Z";
const UP_SHUT = "M8 50 Q26 50 50 50 Q74 50 92 50 L92 -14 L8 -14 Z";
const DN_OPEN = "M8 50 Q26 74 50 78 Q74 74 92 50 L92 114 L8 114 Z";
const DN_SHUT = "M8 50 Q26 50 50 50 Q74 50 92 50 L92 114 L8 114 Z";

/* Lid edges - the thick seam, which travels WITH the lid because in this mark
   the seam IS the eyelid. A seam left behind while the lid closed would draw
   an open eye outline across a shut eye. */
const UP_EDGE_OPEN = "M8 50 Q26 26 50 22 Q74 26 92 50";
const UP_EDGE_SHUT = "M8 50 Q26 50 50 50 Q74 50 92 50";
const DN_EDGE_OPEN = "M8 50 Q26 74 50 78 Q74 74 92 50";
const DN_EDGE_SHUT = "M8 50 Q26 50 50 50 Q74 50 92 50";

/* Two closes inside one run, then a long rest. A single evenly-spaced blink
   reads as a broken loop rather than a face. */
const KEY_TIMES = "0;.13;.22;.31;.55;.64;.73;1";
const SPLINES = Array(7).fill(".4 0 .2 1").join("; ");
const vals = (open, shut) =>
  [open, open, shut, open, open, shut, open, open].join(";");

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const q = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(q.matches);
    apply();
    q.addEventListener ? q.addEventListener("change", apply) : q.addListener(apply);
    return () => {
      q.removeEventListener ? q.removeEventListener("change", apply) : q.removeListener(apply);
    };
  }, []);
  return reduced;
}

export default function XBall({ size = 40, className = "", style, blink = true }) {
  const uid = useId().replace(/:/g, "");
  const reduced = usePrefersReducedMotion();
  const animate = blink && !reduced;

  const ball = `xb-ball-${uid}`;
  const panel = `xb-panel-${uid}`;
  const carve = `xb-carve-${uid}`;
  const seamF = `xb-seam-${uid}`;
  const clip = `xb-clip-${uid}`;
  const lens = `xb-lens-${uid}`;
  const gloss = `xb-gloss-${uid}`;
  const lidAnim = `xb-lid-${uid}`;

  // Every animated path runs off ONE clock - the first <animate> owns the id
  // and the rest begin with it, so the four paths cannot drift apart.
  const lid = (open, shut, first) =>
    animate ? (
      <animate
        attributeName="d"
        {...(first ? { id: lidAnim } : {})}
        begin={first ? `1.2s;${lidAnim}.end+3.4s` : `${lidAnim}.begin`}
        dur="1.15s"
        fill="freeze"
        calcMode="spline"
        keyTimes={KEY_TIMES}
        values={vals(open, shut)}
        keySplines={SPLINES}
      />
    ) : null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={className}
      style={style}
      role="img"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* userSpaceOnUse, NOT percentages - see the gradient trap above. */}
        <radialGradient id={ball} cx="35" cy="28" r="78" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFC58A" />
          <stop offset="48%" stopColor="#F07A16" />
          <stop offset="100%" stopColor="#9B3603" />
        </radialGradient>
        <linearGradient id={panel} x1="20" y1="22" x2="80" y2="78" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFF8F0" />
          <stop offset="100%" stopColor="#FFF5EB" />
        </linearGradient>
        <filter id={carve} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="1.1" dy="1.1" stdDeviation="0.7" floodColor="#FFFFFF" floodOpacity="0.9" />
          <feDropShadow dx="-1.1" dy="-1.1" stdDeviation="0.9" floodColor="#8A4406" floodOpacity="0.55" />
        </filter>
        <filter id={seamF} x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="1.2" dy="1.2" stdDeviation="0.8" floodColor="#FFD9A8" floodOpacity="0.7" />
          <feDropShadow dx="-1.2" dy="-1.2" stdDeviation="1" floodColor="#571C00" floodOpacity="0.8" />
        </filter>
        <clipPath id={clip}>
          <circle cx="50" cy="50" r="46" />
        </clipPath>
        <clipPath id={lens}>
          <path d={LENS} />
        </clipPath>
        <clipPath id={gloss}>
          <path clipRule="evenodd" d={`M4 50 A46 46 0 1 1 96 50 A46 46 0 1 1 4 50 Z ${LENS}`} />
        </clipPath>
      </defs>

      <circle cx="50" cy="50" r="46" fill={`url(#${ball})`} />

      <path d={LENS} fill={`url(#${panel})`} />
      <g clipPath={`url(#${lens})`}>
        <path d={LENS} fill="none" stroke="#5B2200" strokeOpacity="0.38" strokeWidth="7" transform="translate(1.6,2.2)" />
        <path d={LENS} fill="none" stroke="#FFE7C4" strokeOpacity="0.25" strokeWidth="5" transform="translate(-1.4,-1.8)" />
      </g>

      {/* the X, pressed into the panel. Clip and filter on the SAME element, so
          the clip applies AFTER the filter and the bevel is cut at the lid. */}
      <g clipPath={`url(#${lens})`} filter={`url(#${carve})`} fill="none" stroke="#C2600C" strokeLinecap="round">
        <path d="M33 33 L67 67 M67 33 L33 67" strokeWidth="10" />
      </g>

      {/* the lids - drawn last so they close over the eye */}
      <g clipPath={`url(#${clip})`}>
        <path d={UP_OPEN} fill={`url(#${ball})`}>{lid(UP_OPEN, UP_SHUT, true)}</path>
        <path d={DN_OPEN} fill={`url(#${ball})`}>{lid(DN_OPEN, DN_SHUT, false)}</path>
        <g filter={`url(#${seamF})`} fill="none" stroke="#B54F0A" strokeLinecap="round" strokeWidth="4" opacity="0.85">
          <path d={UP_EDGE_OPEN}>{lid(UP_EDGE_OPEN, UP_EDGE_SHUT, false)}</path>
          <path d={DN_EDGE_OPEN}>{lid(DN_EDGE_OPEN, DN_EDGE_SHUT, false)}</path>
        </g>
      </g>

      <g clipPath={`url(#${gloss})`}>
        <ellipse cx="36" cy="28" rx="16" ry="10" fill="#fff" opacity="0.2" transform="rotate(-25 36 28)" />
      </g>
    </svg>
  );
}
